import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

interface Props {
  isEmbedded?: boolean;
}

export default function PrivacyPolicy({ isEmbedded = false }: Props) {
  const year = new Date().getFullYear();
  return (
    <div className={isEmbedded ? "pp-embedded" : "pp-page"}>
      <div className="pp-container">
        <div className="pp-header">
          <h1>Privacy Policy</h1>
          <p className="pp-subtitle">Chính sách Bảo mật / Privacy Policy</p>
          <p className="pp-meta">Effective Date: January 1, 2025 &nbsp;|&nbsp; Last Updated: April 2025</p>
          <p className="pp-meta">Operated by: <strong>Noland Hoang</strong> | Application: <strong>Phanh</strong></p>
        </div>

        <div className="pp-section">
          <h2>1. Introduction / Giới thiệu</h2>
          <p>
            Welcome to <strong>Phanh</strong> ("we", "our", "us"). We operate a professional booking management platform
            accessible via our web and mobile applications ("the Service"). This Privacy Policy explains how we collect, use,
            disclose, and safeguard your information when you use our Service.
          </p>
          <p>
            Chào mừng bạn đến với <strong>Phanh</strong>. Chúng tôi vận hành nền tảng quản lý đặt lịch chuyên nghiệp thông qua các ứng dụng web và di động.
            Chính sách Bảo mật này giải thích cách chúng tôi thu thập, sử dụng, tiết lộ và bảo vệ thông tin của bạn.
          </p>
          <p>
            By using the Service, you agree to the collection and use of information in accordance with this policy.
          </p>
        </div>

        <div className="pp-section">
          <h2>2. Information We Collect / Thông tin chúng tôi thu thập</h2>
          <p>We may collect the following categories of personal information:</p>
          <ul>
            <li><strong>Identity Data:</strong> Full name, username, date of birth, gender.</li>
            <li><strong>Contact Data:</strong> Email address, phone number, mailing address.</li>
            <li><strong>Technical Data:</strong> IP address, browser type, device identifiers, operating system.</li>
            <li><strong>Usage Data:</strong> Pages visited, features used, booking and appointment history within the platform.</li>
            <li><strong>Communications Data:</strong> In-app notifications and system messages.</li>
          </ul>
          <p>
            We collect this information when you register an account, use our features, or interact with our platform. 
            When you use third-party logins (e.g., Google or Facebook via Firebase Auth), we receive your basic profile information (name, email, profile picture) from these providers.
          </p>
        </div>

        <div className="pp-section">
          <h2>3. How We Use Your Information / Cách chúng tôi sử dụng thông tin</h2>
          <p>Your personal information is used <strong>exclusively</strong> for the following purposes:</p>
          <ul>
            <li>To create and manage your account on the Platform.</li>
            <li>To provide, operate, and maintain our Service.</li>
            <li>To send administrative notifications (e.g., booking confirmations, system alerts).</li>
            <li>To detect, prevent, and address technical issues and security threats.</li>
            <li>To comply with applicable legal obligations.</li>
          </ul>
          <p className="pp-highlight">
            ✅ <strong>We do not use your personal information for advertising, marketing to third parties, profiling, or any purpose
            unrelated to the operation of the Phanh platform.</strong>
          </p>
          <p>
            Chúng tôi <strong>không sử dụng</strong> thông tin cá nhân của bạn cho bất kỳ hoạt động quảng cáo, tiếp thị bên thứ ba,
            tạo hồ sơ người dùng hay bất kỳ mục đích nào khác ngoài việc vận hành nền tảng Phanh.
          </p>
          <p>
            <strong>Push Notifications:</strong> With your explicit consent, we may send push notifications (via Firebase Cloud Messaging - FCM) to your device to provide booking updates and administrative alerts. You can opt out at any time via your device settings.
          </p>
        </div>

        <div className="pp-section">
          <h2>4. Data Security / Bảo mật dữ liệu</h2>
          <p>
            We implement industry-standard security measures to protect your personal information:
          </p>
          <ul>
            <li>All passwords are encrypted using <strong>bcrypt</strong> hashing before storage. We do not store plain-text passwords.</li>
            <li>All data transmissions use <strong>HTTPS (TLS/SSL)</strong> encryption.</li>
            <li>Access to your personal data is restricted to authorized personnel only.</li>
            <li>We conduct regular security audits and apply security patches promptly.</li>
          </ul>
          <p>
            Tất cả mật khẩu được mã hóa bằng bcrypt trước khi lưu trữ. Chúng tôi không lưu mật khẩu dạng văn bản thuần túy.
            Toàn bộ truyền tải dữ liệu được bảo mật bằng HTTPS.
          </p>
        </div>

        <div className="pp-section">
          <h2>5. Data Sharing and Disclosure / Chia sẻ và tiết lộ dữ liệu</h2>
          <p>
            We do not sell, trade, rent, or transfer your personal information to third parties, except in the following limited circumstances:
          </p>
          <ul>
            <li><strong>Service Providers:</strong> We engage trusted third-party service providers (e.g., cloud hosting via Render/Vercel, database hosting via MongoDB Atlas, authentication via Firebase/Meta)
              who process data solely on our behalf and under strict confidentiality agreements.</li>
            <li><strong>Legal Requirements:</strong> We may disclose your information if required by law, court order, or governmental authority.</li>
            <li><strong>Business Transfer:</strong> In the event of a merger or acquisition, user data may be transferred with full notice to users.</li>
          </ul>
          <p className="pp-highlight">
            <strong>Platform vs. Business Relationship:</strong> Phanh provides the platform for independent businesses to manage their bookings. When you book a service with a specific business through Phanh, that business acts as the "Data Controller" for your booking information. We process this data on their behalf. Businesses are responsible for handling your data in accordance with their own privacy practices and applicable laws.
          </p>
        </div>

        <div className="pp-section">
          <h2>6. Compliance with Canadian Privacy Law / Tuân thủ Luật Bảo mật Canada</h2>
          <p>
            We operate in compliance with the following Canadian privacy legislation:
          </p>
          <ul>
            <li>
              <strong>Personal Information Protection and Electronic Documents Act (PIPEDA):</strong> We collect, use, and disclose
              personal information only with your knowledge and consent, and only for purposes a reasonable person would consider appropriate
              in the circumstances.
            </li>
            <li>
              <strong>Canada's Anti-Spam Legislation (CASL):</strong> We do not send commercial electronic messages without your explicit consent.
              System notifications related to your use of the Service are transactional and exempt from CASL requirements.
            </li>
            <li>
              <strong>Alberta's Personal Information Protection Act (PIPA) and Quebec's Law 25 (Law 64):</strong> Users in Alberta and Quebec
              have additional rights, including the right to access, correct, and request deletion of their personal data.
            </li>
            <li>
              <strong>Right to Access:</strong> You have the right to request access to the personal information we hold about you.
              We will respond to such requests within 30 days.
            </li>
            <li>
              <strong>Right to Correction:</strong> You have the right to request correction of inaccurate or incomplete personal information.
            </li>
            <li>
              <strong>Right to Withdraw Consent:</strong> You may withdraw your consent to our collection and use of your personal information at any time,
              subject to legal and contractual restrictions.
            </li>
            <li>
              <strong>Data Breach Notification:</strong> In the event of a privacy breach that poses a real risk of significant harm,
              we will notify affected individuals and the Office of the Privacy Commissioner of Canada (OPC) as required under PIPEDA.
            </li>
          </ul>
        </div>

        <div className="pp-section">
          <h2>7. Your Rights / Quyền của bạn</h2>
          <p>Regardless of your location, you have the following rights:</p>
          <ul>
            <li><strong>Access:</strong> You may request a copy of the personal information we hold about you.</li>
            <li><strong>Correction:</strong> You may update or correct inaccurate information via your account profile page.</li>
            <li><strong>Deletion:</strong> You may permanently delete your account and all associated data via the Profile &gt; Danger Zone option in the app.</li>
            <li><strong>Portability:</strong> You may request your data in a structured, machine-readable format.</li>
            <li><strong>Objection:</strong> You may object to the processing of your data in certain circumstances.</li>
          </ul>
          <p>To exercise these rights, contact us at the address below.</p>
        </div>

        <div className="pp-section">
          <h2>8. Cookies and Tracking / Cookie và theo dõi</h2>
          <p>
            We use essential cookies and local storage to maintain your login session and application preferences.
            We do not use advertising cookies or third-party tracking technologies.
          </p>
        </div>

        <div className="pp-section">
          <h2>9. Data Retention / Lưu giữ dữ liệu</h2>
          <p>
            We retain your personal information for as long as your account is active or as needed to provide you with the Service.
            Upon account deletion, your personal data will be permanently removed from our systems within <strong>30 days</strong>,
            except where retention is required for legal compliance.
          </p>
        </div>

        <div className="pp-section">
          <h2>10. Children's Privacy / Bảo mật trẻ em</h2>
          <p>
            Our Service is not directed to individuals under the age of 13 (or 16 in Quebec as per Law 25).
            We do not knowingly collect personal information from children without verifiable parental consent.
          </p>
        </div>

        <div className="pp-section">
          <h2>11. Changes to This Policy / Thay đổi chính sách</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy
            within the Application and updating the "Last Updated" date at the top of this page.
            Continued use of the Service after changes constitutes your acceptance of the revised policy.
          </p>
        </div>

        <div className="pp-section">
          <h2>12. Contact Us / Liên hệ</h2>
          <p>If you have any questions, concerns, or requests regarding this Privacy Policy, please contact:</p>
          <div className="pp-contact">
            <p><strong>Noland Hoang</strong> (Hoàng Phi Anh)</p>
            <p>Application: Phanh Booking Management Platform</p>
          </div>
        </div>

        {!isEmbedded && (
          <div className="pp-footer">
            <p>© {year} Phanh. Copyright by <strong>Noland Hoang</strong> (Hoàng Phi Anh). All rights reserved.</p>
            <Link to="/register" className="pp-back-link">← Back to Register</Link>
          </div>
        )}
      </div>
    </div>
  );
}
